import { useState, useEffect, useCallback } from 'react';
import { 
  getDashboardStats, 
  getPendingMeetings,
  getFilteredMeetings,
  getMeetingById,
  approveMeeting,
  rejectMeeting,
  editMeeting,
  getUsers,
  getUserById,
  updateUserRole,
  updateUserStatus,
  updateUserNotes,
  createUserAsAdmin,
  createMeetingForUser,
  searchUsersByQuery
} from '../services/api/adminApi';
import { useAuth } from './useAuth';
import { 
  initAdminSocket, 
  listenToAdminEvents, 
  cleanupAdminSocket 
} from '../services/socket/adminSocketService';
import { toast } from 'react-hot-toast';

export const useAdmin = () => {
  const { isAdmin } = useAuth();
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Initialize admin socket
  useEffect(() => {
    if (isAdmin) {
      initAdminSocket();
      
      // Listen for admin events
      const cleanup = listenToAdminEvents({
        onMeetingCreated: () => {
          // Refresh pending count
          loadDashboardStats();
          toast('New meeting created');
        },
        onMeetingApproved: () => {
          // Refresh counts when a meeting is approved
          loadDashboardStats();
        },
        onMeetingRejected: () => {
          // Refresh counts when a meeting is rejected
          loadDashboardStats();
        },
        onPendingCount: (data) => {
          setPendingCount(data.count);
        }
      });
      
      return () => {
        cleanup();
        cleanupAdminSocket();
      };
    }
  }, [isAdmin]);
  
  // Load dashboard stats
  const loadDashboardStats = useCallback(async () => {
    if (!isAdmin) return;
    
    setIsLoadingStats(true);
    try {
      const stats = await getDashboardStats();
      setDashboardStats(stats);
      setPendingCount(stats.meetings.pending);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isAdmin]);
  
  // Get filtered meetings
  const getFilteredMeetingsByStatus = useCallback(async (
    status: string = 'pending',
    page: number = 1,
    limit: number = 10,
    skipCache: boolean = false
  ) => {
    if (!isAdmin) return { success: false, meetings: [], pagination: { total: 0, page, limit, pages: 0 } };
    
    try {
      return await getFilteredMeetings(status, page, limit, skipCache);
    } catch (error) {
      console.error(`Failed to load ${status} meetings:`, error);
      return { 
        success: false, 
        meetings: [], 
        pagination: { total: 0, page, limit, pages: 0 },
        error: error 
      };
    }
  }, [isAdmin]);
  
  // Approve meeting
  const approveSelectedMeeting = useCallback(async (
    id: string,
    notes?: string
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await approveMeeting(id, { notes });
      
      if (result.success) {
        toast.success('Meeting approved successfully');
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to approve meeting');
      return { success: false };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Reject meeting
  const rejectSelectedMeeting = useCallback(async (
    id: string,
    reason: string,
    notes?: string
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await rejectMeeting(id, { reason, notes });
      
      if (result.success) {
        toast.success('Meeting rejected successfully');
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to reject meeting');
      return { success: false };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Edit meeting
  const editSelectedMeeting = useCallback(async (
    id: string,
    data: {
      title?: string;
      description?: string;
      date?: string;
      time?: string;
      approve?: boolean;
      notes?: string;
    }
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await editMeeting(id, data);
      
      if (result.success) {
        toast.success(data.approve 
          ? 'Meeting edited and approved successfully' 
          : 'Meeting edited successfully'
        );
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to edit meeting');
      return { success: false };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Create new user (without OTP)
  const createNewUser = useCallback(async (
    name: string,
    phone: string
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await createUserAsAdmin({ name, phone });
      
      if (result.success) {
        toast.success('User created successfully');
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create user');
      return { success: false, error };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Create meeting for a selected user
  const createMeetingForSelectedUser = useCallback(async (
    userId: string,
    meetingData: {
      title: string;
      description: string;
      date: string;
      time: string;
      coHosts?: Array<{name: string; phone: string}>;
    }
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await createMeetingForUser({ 
        userId, 
        ...meetingData 
      });
      
      if (result.success) {
        toast.success('Meeting created successfully');
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create meeting');
      return { success: false, error };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Search users by name or phone
  const searchUsersByName = useCallback(async (
    query: string,
    limit: number = 10
  ) => {
    if (!isAdmin) return { success: false, users: [] };
    
    try {
      const result = await searchUsersByQuery(query, limit);
      return result;
    } catch (error: any) {
      console.error('Error searching users:', error);
      return { success: false, users: [] };
    }
  }, [isAdmin]);
  
  // Update user role
  const updateUserRoleById = useCallback(async (
    id: string,
    role: 'user' | 'admin'
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await updateUserRole(id, { role });
      
      if (result.success) {
        toast.success(`User role updated to ${role} successfully`);
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user role');
      return { success: false };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Update user status
  const updateUserStatusById = useCallback(async (
    id: string,
    isActive: boolean
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await updateUserStatus(id, { isActive });
      
      if (result.success) {
        toast.success(`User ${isActive ? 'activated' : 'deactivated'} successfully`);
        loadDashboardStats();
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user status');
      return { success: false };
    }
  }, [isAdmin, loadDashboardStats]);
  
  // Update user notes
  const updateUserNotesById = useCallback(async (
    id: string,
    notes: string
  ) => {
    if (!isAdmin) return { success: false };
    
    try {
      const result = await updateUserNotes(id, { notes });
      
      if (result.success) {
        toast.success('User notes updated successfully');
      }
      
      return result;
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update user notes');
      return { success: false };
    }
  }, [isAdmin]);
  
  // Return admin-specific functions and data
  return {
    isAdmin,
    dashboardStats,
    isLoadingStats,
    pendingCount,
    loadDashboardStats,
    getPendingMeetings,
    getFilteredMeetingsByStatus,
    getMeetingById,
    approveSelectedMeeting,
    rejectSelectedMeeting,
    editSelectedMeeting,
    getUsers,
    getUserById,
    updateUserRoleById,
    updateUserStatusById,
    updateUserNotesById,
    createNewUser,
    createMeetingForSelectedUser,
    searchUsersByName
  };
};
